import { useEffect, useMemo, useRef, useState } from 'react';
import { Film, Star } from 'lucide-react';
import AutocompleteSelect, { AutocompleteSelectOption } from '@/components/ui/AutocompleteSelect';
import { useTranslation } from '@/hooks';
import {
  contentDetailsService,
  TmdbMatchCriteria,
  TmdbSearchKind,
  TmdbSearchResult,
} from '@/services/contentDetailsService';

interface TmdbContentAutocompleteProps {
  kind: TmdbSearchKind;
  value: string;
  selectedTitle?: string;
  selectedYear?: number;
  loading?: boolean;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  matchCriteria?: TmdbMatchCriteria;
  onChange: (tmdbId: string) => void;
}

const searchDelayMs = 350;

const TmdbContentAutocomplete = ({
  kind,
  value,
  selectedTitle,
  selectedYear,
  loading = false,
  disabled = false,
  label,
  helperText,
  matchCriteria,
  onChange,
}: TmdbContentAutocompleteProps) => {
  const { t } = useTranslation();
  const labels = t.tmdbSearch;
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<TmdbSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string>();
  const requestSequence = useRef(0);
  const matchImdbId = matchCriteria?.imdbId?.trim();
  const matchTitle = matchCriteria?.title?.trim();
  const matchYear = matchCriteria?.year;

  useEffect(() => {
    const normalizedQuery = query.trim();
    const canMatch = Boolean(matchImdbId || matchTitle);
    if (!normalizedQuery && !canMatch) {
      requestSequence.current += 1;
      setResults([]);
      setSearching(false);
      setError(undefined);
      return;
    }

    const sequence = ++requestSequence.current;
    const timeout = window.setTimeout(async () => {
      setSearching(true);
      setError(undefined);
      try {
        const matches = normalizedQuery
          ? await contentDetailsService.searchTmdb(kind, normalizedQuery)
          : await contentDetailsService.matchTmdb(kind, {
              imdbId: matchImdbId,
              title: matchTitle,
              year: matchYear,
            });
        if (requestSequence.current === sequence) {
          setResults(matches);
        }
      } catch {
        if (requestSequence.current === sequence) {
          setResults([]);
          setError(labels.error);
        }
      } finally {
        if (requestSequence.current === sequence) {
          setSearching(false);
        }
      }
    }, searchDelayMs);

    return () => window.clearTimeout(timeout);
  }, [kind, labels.error, matchImdbId, matchTitle, matchYear, query]);

  const selectedFallback = useMemo<TmdbSearchResult | undefined>(() => (
    value
      ? {
          tmdbId: value,
          title: selectedTitle || `TMDB #${value}`,
          year: selectedYear,
        }
      : undefined
  ), [selectedTitle, selectedYear, value]);

  const availableResults = useMemo(() => {
    if (query.trim() || (!value && results.length > 0)) {
      return results;
    }
    return selectedFallback ? [selectedFallback] : [];
  }, [query, results, selectedFallback, value]);

  const options = useMemo<AutocompleteSelectOption[]>(() => availableResults.map((result) => ({
    value: result.tmdbId,
    label: result.year ? `${result.title} (${result.year})` : result.title,
  })), [availableResults]);

  const resultsById = useMemo(
    () => new Map(availableResults.map((result) => [result.tmdbId, result])),
    [availableResults],
  );

  const renderResult = (option: AutocompleteSelectOption, compact = false) => {
    const result = resultsById.get(option.value);
    if (!result) return option.label;
    return (
      <span className="flex min-w-0 items-center gap-3">
        <span className={`${compact ? 'h-10 w-7' : 'h-16 w-11'} flex-none overflow-hidden rounded border border-gray-200 bg-gray-100`}>
          {result.posterImageUrl ? (
            <img src={result.posterImageUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-gray-400">
              <Film className="h-4 w-4" />
            </span>
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="truncate font-medium text-gray-900">{result.title}</span>
            {result.year && <span className="flex-none text-xs text-gray-500">{result.year}</span>}
          </span>
          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
            <span>TMDB #{result.tmdbId}</span>
            {result.rating != null && result.rating > 0 && (
              <span className="inline-flex items-center gap-1">
                <Star className="h-3 w-3 fill-amber-400 text-amber-500" />
                {result.rating.toFixed(1)}
              </span>
            )}
            {result.originalLanguage && <span>{result.originalLanguage.toUpperCase()}</span>}
            {result.adult && <span className="font-medium text-red-600">18+</span>}
          </span>
          {!compact && result.originalTitle && result.originalTitle !== result.title && (
            <span className="mt-0.5 block truncate text-xs text-gray-500">{result.originalTitle}</span>
          )}
          {!compact && result.overview && (
            <span className="mt-1 block truncate text-xs text-gray-600">{result.overview}</span>
          )}
        </span>
      </span>
    );
  };

  return (
    <AutocompleteSelect
      label={label || (kind === 'movie' ? labels.movieLabel : labels.tvShowLabel)}
      placeholder={labels.placeholder}
      helperText={helperText || labels.helper}
      emptyMessage={query.trim() || matchCriteria ? labels.noResults : labels.startTyping}
      value={value}
      options={options}
      onSearch={setQuery}
      onChange={onChange}
      loading={searching || loading}
      disabled={disabled}
      error={error}
      renderOption={(option) => renderResult(option)}
      renderSelectedOption={(option) => renderResult(option, true)}
    />
  );
};

export default TmdbContentAutocomplete;
